import { useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  addMembersToGroup,
  addMembersToGroupUserPortal,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { DirectoryGroupMember } from '../../../../../types';
import GeneralDialog from '../../../../shared/general-dialog';
import { GridColDef } from '@mui/x-data-grid-pro';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  UserViewRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { emailRegex } from '../../../../utils/regular-expressions';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import { Messages } from '../../../../utils/message-constants';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Groups from '@mui/icons-material/Groups';
import Person from '@mui/icons-material/Person';
import PersonOff from '@mui/icons-material/PersonOff';

interface BulkAddGroupMembersProps {
  open: boolean;
  handleClose: () => void;
  groupEmail: string;
  changeLogRoles: string[];
  memberList: DirectoryGroupMember[];
  userView: boolean;
  directoryRoles: string[];
  userViewRoles: string[];
}

export default function BulkAddGroupMembersDialog({
  open,
  handleClose,
  groupEmail,
  changeLogRoles,
  memberList,
  userView,
  directoryRoles,
  userViewRoles,
}: BulkAddGroupMembersProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<
    DirectoryGroupMember[]
  >([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [autoCompleteResponse, setAutoCompleteResponse] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [isCheckboxSelected, setIsCheckboxSelected] = useState(false);
  const [confirmAddAll, setConfirmAddAll] = useState(false);
  const [confirmationError, setConfirmationError] = useState(false);

  // Check if user has permission to add all current and future users within domain
  const canAddAllCurrentAndFutureUsers = isAbleToSeeUIElement(
    userView ? userViewRoles : directoryRoles,
    userView
      ? UserViewRoles.AddAllCurrentAndFutureUsersWithinDomain.value
      : DirectoryRoles.AddAllCurrentAndFutureUsersWithinDomain.value
  );

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      addMembersToGroup(groupEmail, selectedMembers, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Added(
          selectedMembers.length === 1 ? 'Group member' : 'Group members'
        ),
      });

      queryClient.setQueryData(
        [UserKeys.GroupMembers, groupEmail],
        (oldData: DirectoryGroupMember[] | undefined) => {
          if (!oldData) {
            return oldData;
          } else {
            return [...oldData, ...selectedMembers].sort((a, b) =>
              a.email.localeCompare(b.email)
            );
          }
        }
      );
      setSelectedRows([]);
      setSelectedMembers([]);
      setAutoCompleteResponse('');
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Adding(
          selectedMembers.length === 1
            ? 'the selected group member'
            : 'the selectd group members'
        ),
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.group_details.create_group_member, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groups,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: userPortalMutate, isPending: userPortalPending } =
    useMutation({
      mutationFn: () =>
        addMembersToGroupUserPortal(groupEmail, selectedMembers),
      onSuccess: () => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: Messages.Success.Added(
            selectedMembers.length === 1 ? 'Group member' : 'Group members'
          ),
        });
        queryClient.setQueryData(
          [UserKeys.GroupMembers, groupEmail],
          (oldData: DirectoryGroupMember[] | undefined) => {
            if (!oldData) {
              return oldData;
            } else {
              return [...oldData, ...selectedMembers].sort((a, b) =>
                a.email.localeCompare(b.email)
              );
            }
          }
        );
        setSelectedRows([]);
        setSelectedMembers([]);
        setAutoCompleteResponse('');
        handleClose();
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage: Messages.Errors.Network.Adding(
            selectedMembers.length === 1
              ? 'the selected group member'
              : 'the selectd group members'
          ),
        });
      },
      onSettled: () => {
        createEvent(analytics.userView.group_details.create_group_members, {
          module: ModuleList.modules.userView.name,
          section: ModuleList.modules.userView.sections.groupDetails,
        });
      },
    });

  function handleSubmit() {
    if (isCheckboxSelected && !confirmAddAll) {
      setConfirmationError(true);
      return;
    }
    setConfirmationError(false);

    const needsChangeLog =
      (isCheckboxSelected &&
        !userView &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.AddAllCurrentAndFutureUsersWithinDomain.value
        )) ||
      (!isCheckboxSelected &&
        !userView &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.AddMemberToGroup.value
        ));

    if (needsChangeLog) {
      setOpenChangeLogDialog(true);
    } else {
      if (userView) {
        userPortalMutate();
      } else {
        mutate();
      }
    }
  }

  const memberListEmails = memberList.map((member) => member.email);

  function addSelection(email: string, type?: string) {
    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address');
    } else if (email === groupEmail) {
      setErrorMessage('Cannot add the group as a member of itself.');
    } else if (memberListEmails.includes(email)) {
      setErrorMessage('User/Group is already a member of this group.');
    } else if (selectedMembers.some((member) => member.email === email)) {
      setErrorMessage('User/Group has already been selected.');
    } else {
      setErrorMessage('');
      setAutoCompleteResponse('');
      const newGroupMember: DirectoryGroupMember = {
        email: email,
        role: 'MEMBER',
        canActOn: true,
        type: type ? type : 'user',
      };
      setSelectedMembers([...selectedMembers, newGroupMember]);
    }
  }

  function removeSelection(emails: string[]) {
    const newSelectedMembers = selectedMembers.filter(
      (member) => !emails.includes(member.email)
    );
    setSelectedMembers(newSelectedMembers);
    setSelectedRows([]);
  }

  // Function to handle checkbox state change
  function handleCheckboxChange(checked: boolean) {
    setIsCheckboxSelected(checked);

    if (checked) {
      const wildcardMember: DirectoryGroupMember = {
        email: '*',
        role: 'MEMBER',
        canActOn: true,
        type: 'customer',
      };

      setSelectedMembers((prev) => {
        // Ensure "*" is not duplicated
        if (!prev.some((member) => member.email === '*')) {
          return [...prev, wildcardMember];
        }
        return prev;
      });
    } else {
      // Remove the wildcard "*" but keep manually added users
      setSelectedMembers((prev) =>
        prev.filter((member) => member.email !== '*')
      );
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'email',
      headerName: 'Email',
      editable: false,
      sortable: false,
      flex: 1,
      display: 'flex',
      renderCell: (params) => {
        const emailDisplay =
          params.row.email === '*'
            ? 'All current and future users within domain'
            : params.row.email;
        const memberIndex = selectedMembers
          .map((member) => member.email)
          .indexOf(params.value);

        return (
          <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            sx={{ mr: 4 }}
          >
            <Typography variant="body2">{emailDisplay}</Typography>

            {selectedMembers
              ?.map((member) => member.email)
              .includes(params.value) && (
              <TextField
                inputProps={{
                  id: `member-role-${params.value}`,
                }}
                InputLabelProps={{
                  htmlFor: `member-role-${params.value}`,
                }}
                label="Role"
                select
                value={selectedMembers[memberIndex].role}
                onChange={(event) => {
                  const newArray = [...selectedMembers];
                  newArray[memberIndex].role = event.target.value as
                    | 'MEMBER'
                    | 'MANAGER'
                    | 'OWNER';
                  setSelectedMembers(newArray);
                }}
                size="small"
                margin="none"
                defaultValue="MEMBER"
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="MEMBER">Member</MenuItem>
                {selectedMembers[memberIndex].type === 'user' ? (
                  <MenuItem value="MANAGER">Manager</MenuItem>
                ) : null}
                {selectedMembers[memberIndex].type === 'user' ? (
                  <MenuItem value="OWNER">Owner</MenuItem>
                ) : null}
              </TextField>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'type',
      headerName: 'Type',
      editable: false,
      sortable: true,
      filterable: false,
      display: 'flex',
      renderCell: (params) => {
        const avatar = (
          <Stack flexDirection="row" gap={1}>
            {params.row.type === 'group' ? (
              <Tooltip arrow title="Group">
                <Groups />
              </Tooltip>
            ) : (
              <Tooltip arrow title="User">
                <Person />
              </Tooltip>
            )}
          </Stack>
        );
        return avatar;
      },
    },
  ];

  function CustomToolbar() {
    return (
      <SharedToolbar
        noColumn
        noFilter
        noQuickFilter
        secondaryButtons={
          <SharedIconButton
            variant="delete"
            onClick={() => removeSelection(selectedRows)}
            disabled={selectedRows.length === 0}
            tooltipLabel="Delete"
          />
        }
      />
    );
  }

  return (
    <GeneralDialog
      open={open}
      handleClose={() => {
        setAutoCompleteResponse('');
        setErrorMessage('');
        setSelectedRows([]);
        setSelectedMembers([]);
        setIsCheckboxSelected(false);
        setConfirmAddAll(false);
        setConfirmationError(false);
        handleClose();
      }}
      title="Add Members"
      maxWidth="md"
      dialogContent={
        <Stack gap={2}>
          <Typography variant="body2" paddingLeft={2}>
            Select any groups or users you'd like added as members to this
            group.
          </Typography>
          <Stack paddingLeft={2} alignItems="flex-start">
            <Tooltip
              arrow
              title={
                !canAddAllCurrentAndFutureUsers
                  ? Messages.Errors.Permission.Acting(
                      'Add all current and future users within domain'
                    )
                  : memberList?.some((member) => member.email === '*')
                  ? Messages.Info.AllUsersAlreadyAdded(groupEmail)
                  : ''
              }
            >
              <span>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        isCheckboxSelected ||
                        memberList?.some((member) => member.email === '*')
                      }
                      onChange={(e) => handleCheckboxChange(e.target.checked)}
                      disabled={
                        !canAddAllCurrentAndFutureUsers ||
                        memberList?.some((member) => member.email === '*')
                      }
                    />
                  }
                  label="Add all current and future users within domain."
                />
              </span>
            </Tooltip>
            <Collapse in={isCheckboxSelected && canAddAllCurrentAndFutureUsers}>
              <Alert severity="warning" sx={{ alignItems: 'start', mt: 1 }}>
                Users will be automatically added as they join your
                organization.
                <FormControl
                  required
                  error={confirmationError}
                  sx={{ display: 'block', mt: 1, pl: 1.5 }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="warning"
                        checked={confirmAddAll}
                        onChange={(e) => {
                          setConfirmAddAll(e.target.checked);
                          if (e.target.checked) {
                            setConfirmationError(false);
                          }
                        }}
                      />
                    }
                    label="I understand that all current and future users will be added to this group."
                  />
                  {confirmationError && (
                    <FormHelperText sx={{ ml: 0 }}>
                      Confirmation is required to add all users.
                    </FormHelperText>
                  )}
                </FormControl>
              </Alert>
            </Collapse>
          </Stack>
          <Stack
            flexDirection={'row'}
            gap={1}
            paddingLeft={2}
            alignItems="flex-start"
          >
            <Box sx={{ width: 500 }}>
              <SharedAutocomplete
                freeSolo
                sources={['users', 'groups']}
                autocompleteValue={autoCompleteResponse}
                setAutoCompleteValue={setAutoCompleteResponse}
                errors={!!errorMessage}
                helperText={errorMessage || ' '}
                onSelection={(value, type) => {
                  if (!value) return;
                  addSelection(value, type);
                }}
              />
            </Box>
          </Stack>
          <Box sx={{ height: 500, pl: 2, pr: 2 }}>
            <SharedDataGridClient
              slots={{
                toolbar: CustomToolbar,
                ...createGridOverlays(
                  'Search Above',
                  <PersonOff />,
                  'Try adding user or group.'
                ),
              }}
              pageSizeOptions={[100]}
              getRowId={(row) => row.email}
              checkboxSelection
              disableColumnMenu
              keepNonExistentRowsSelected
              columns={columns}
              rows={selectedMembers}
              setSelectedRows={setSelectedRows}
              selectedRows={selectedRows}
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
              }}
            />
          </Box>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        loading: isPending || userPortalPending,
        action: handleSubmit,
        disabled: selectedMembers.length === 0 && !isCheckboxSelected,
        title: 'Add',
      }}
    />
  );
}
